import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserMetadata } from '../../../context/UserMetadataContext';
import { PageHeader } from '../../shared';
import RiskSummaryCards from './privilege-creep/RiskSummaryCards';
import RiskHeatMap from './privilege-creep/RiskHeatMap';
import TrendChart from './privilege-creep/TrendChart';
import PeerComparisonCharts from './privilege-creep/PeerComparisonCharts';
import EnhancedUserTable from './privilege-creep/EnhancedUserTable';
import AIInsightsSidebar from './privilege-creep/AIInsightsSidebar';
import FilterBar from './privilege-creep/FilterBar';
import ActiveFiltersBar from './privilege-creep/ActiveFiltersBar';

const PrivilegeCreepDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [peerComparisonData, setPeerComparisonData] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [aiInsights, setAiInsights] = useState(null);
  const [filters, setFilters] = useState({
    department: 'all',
    riskLevel: 'all',
    classification: null,
    minRiskScore: null,
    maxRiskScore: null,
    limit: 50,
    offset: 0
  });

  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();

  const baseUrl = process.env.REACT_APP_API_URL || 'https://api-dev.trustx.cloud/profile360';
  const tenantName = userMetadata?.company || 'trustx';

  // Fetch static dashboard data once on mount
  useEffect(() => {
    fetchStaticDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch filtered user data when filters change
  useEffect(() => {
    fetchUsersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Fetch static dashboard data (summary, heatmap, trends, peer) - only on mount
  const fetchStaticDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = await getAccessTokenSilently();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-Tenant-Name': tenantName
      };

      // Fetch static dashboard data in parallel
      const [summaryRes, heatmapRes, trendsRes, peerRes] = await Promise.all([
        fetch(`${baseUrl}/api/privilege-creep/summary`, { headers }),
        fetch(`${baseUrl}/api/privilege-creep/heatmap`, { headers }),
        fetch(`${baseUrl}/api/privilege-creep/trends?days=30`, { headers }),
        fetch(`${baseUrl}/api/privilege-creep/peer-comparison`, { headers })
      ]);

      // Parse responses
      const summary = await summaryRes.json();
      const heatmap = await heatmapRes.json();
      const trends = await trendsRes.json();
      const peer = await peerRes.json();

      // Update state
      if (summary.success) setSummaryData(summary.data);
      if (heatmap.success) setHeatmapData(heatmap.data);
      if (trends.success) setTrendsData(trends.data.trends);
      if (peer.success) setPeerComparisonData(peer.data.by_group);

    } catch (err) {
      console.error('Error fetching static dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filtered user data - called when filters change
  const fetchUsersData = async () => {
    try {
      setUsersLoading(true);

      const accessToken = await getAccessTokenSilently();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-Tenant-Name': tenantName
      };

      const usersRes = await fetch(buildUsersUrl(), { headers });
      const users = await usersRes.json();

      if (users.success) {
        setUsersData(users.data);
        setTotalUsers(users.total || users.data.length);
        // Generate AI insights from users data
        generateAIInsights(users.data);
      }

    } catch (err) {
      console.error('Error fetching users data:', err);
      // Don't set global error, just log it
    } finally {
      setUsersLoading(false);
    }
  };

  const buildUsersUrl = () => {
    let url = `${baseUrl}/api/privilege-creep/insights?limit=${filters.limit}&offset=${filters.offset}`;

    if (filters.classification) {
      url += `&classification=${filters.classification}`;
    }
    if (filters.minRiskScore) {
      url += `&min_risk_score=${filters.minRiskScore}`;
    }
    if (filters.maxRiskScore) {
      url += `&max_risk_score=${filters.maxRiskScore}`;
    }

    return url;
  };

  const generateAIInsights = (users) => {
    if (!users || users.length === 0) {
      setAiInsights(null);
      return;
    }

    // Extract top risk indicators
    const highRiskUsers = users.filter(u => u.risk_score >= 8);
    const moderateRiskUsers = users.filter(u => u.risk_score >= 6 && u.risk_score < 8);
    const excessiveAccessUsers = users.filter(u => u.groups_above_peer_avg >= 10);
    const privilegedUnusedUsers = users.filter(u =>
      u.privileged_group_count > 0 && u.unused_apps >= 5
    );

    const insights = {
      topRiskIndicators: [
        highRiskUsers.length > 0 && `${highRiskUsers.length} users with critical risk scores (8+)`,
        excessiveAccessUsers.length > 0 && `${excessiveAccessUsers.length} users exceed peer average by 10+ groups`,
        privilegedUnusedUsers.length > 0 && `${privilegedUnusedUsers.length} privileged users with 5+ unused apps`
      ].filter(Boolean),
      recommendedActions: {
        priority: highRiskUsers.length > 0 ? 'HIGH' : moderateRiskUsers.length > 0 ? 'MEDIUM' : 'LOW',
        actions: [
          highRiskUsers.length > 0 && `Review ${highRiskUsers.length} critical users immediately`,
          moderateRiskUsers.length > 0 && `Schedule access reviews for ${moderateRiskUsers.length} high-risk users`,
          privilegedUnusedUsers.length > 0 && `Remove unused privileged access for ${privilegedUnusedUsers.length} users`
        ].filter(Boolean)
      }
    };

    setAiInsights(insights);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => {
      // If new filters include classification, minRiskScore, or maxRiskScore (from risk cards),
      // clear all risk filters to avoid conflicts
      const isRiskCardFilter = newFilters.hasOwnProperty('classification') ||
                                newFilters.hasOwnProperty('minRiskScore') ||
                                newFilters.hasOwnProperty('maxRiskScore');

      if (isRiskCardFilter) {
        // Clear all risk filters, then apply new values
        return {
          ...prev,
          classification: null,
          minRiskScore: null,
          maxRiskScore: null,
          ...newFilters,
          offset: 0
        };
      }

      // For other filters, just merge
      return {
        ...prev,
        ...newFilters,
        offset: 0
      };
    });
  };

  const handleClearFilter = (filterKey) => {
    const clearedFilters = { ...filters };
    if (filterKey === 'classification') {
      clearedFilters.classification = null;
    } else if (filterKey === 'minRiskScore') {
      clearedFilters.minRiskScore = null;
    } else if (filterKey === 'maxRiskScore') {
      clearedFilters.maxRiskScore = null;
    } else if (filterKey === 'department') {
      clearedFilters.department = 'all';
    }
    clearedFilters.offset = 0;
    setFilters(clearedFilters);
  };

  const handleClearAllFilters = () => {
    setFilters({
      department: 'all',
      riskLevel: 'all',
      classification: null,
      minRiskScore: null,
      maxRiskScore: null,
      limit: 50,
      offset: 0
    });
  };

  const handlePageChange = (offset) => {
    setFilters(prev => ({ ...prev, offset }));
  };

  const handleUserAction = (action, userId) => {
    console.log(`Action ${action} for user ${userId}`);
    // TODO: Implement user actions (review, remediate, etc.)
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading Privilege Creep Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="subtitle2" fontWeight={700}>
            Error Loading Dashboard
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Privilege Creep Detection"
        description="AI-powered analysis of user access privileges and risk assessment"
        pillarId="risk"
      />

      {/* Risk Summary Cards */}
      <Box sx={{ mb: 2 }}>
        <RiskSummaryCards data={summaryData} onCardClick={handleFilterChange} />
      </Box>

      {/* Active Filters Bar */}
      <ActiveFiltersBar
        filters={filters}
        onClearFilter={handleClearFilter}
        onClearAll={handleClearAllFilters}
      />

      {/* Filter Bar */}
      <Box sx={{ mb: 2 }}>
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          peerData={peerComparisonData}
        />
      </Box>

      {/* ── Analytics: HeatMap + Trend + Peer side by side ──── */}
      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 xs={12} lg={6}>
          <RiskHeatMap data={heatmapData} />
        </Grid2>
        <Grid2 xs={12} lg={6}>
          <TrendChart data={trendsData} />
        </Grid2>
      </Grid2>

      {/* Peer Comparison */}
      <Box sx={{ mb: 2 }}>
        <PeerComparisonCharts data={peerComparisonData} />
      </Box>

      {/* Enhanced User Table with AI Insights Sidebar */}
      <Grid2 container spacing={2}>
        <Grid2 xs={12} lg={aiInsights ? 9 : 12}>
          <EnhancedUserTable
            users={usersData}
            total={totalUsers}
            loading={usersLoading}
            onUserAction={handleUserAction}
            onPageChange={handlePageChange}
            currentOffset={filters.offset}
            limit={filters.limit}
          />
        </Grid2>
        {aiInsights && (
          <Grid2 xs={12} lg={3}>
            <AIInsightsSidebar insights={aiInsights} />
          </Grid2>
        )}
      </Grid2>
    </Box>
  );
};

export default PrivilegeCreepDashboard;
